import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, StatusPill } from "../ui-bj/Primitives";
import { API } from "../../context/AuthContext";
import { PRODUCTS } from "../../config/products";
import { Calculator } from "@phosphor-icons/react";
import { relabel } from "../../lib/displayNames";

const TOKEN_KEY = "bj_admin_token";
const tokenCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` } });
const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Invoice Calculator — one live form per product recipe. Uses the backend
 * billing engine as the single source of pricing truth; the frontend just
 * ships the inputs and renders totals.
 */
export const CalculatorPanel = ({ onCreated }) => {
  const [productKey, setProductKey] = useState(PRODUCTS[0].key);
  const [months, setMonths] = useState(0);
  const [includeSetup, setIncludeSetup] = useState(true);
  const [capitalSecuredDollars, setCapitalSecuredDollars] = useState(0);
  const [placements, setPlacements] = useState(1);
  const [applications, setApplications] = useState(1);
  const [includeResearch, setIncludeResearch] = useState(true);
  const [stateFilingFeeDollars, setStateFilingFeeDollars] = useState(0);
  const [clientName, setClientName] = useState("");
  const [clientId, setClientId] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  const linesInput = useMemo(() => {
    const li = { product_key: productKey };
    if (productKey === "financial_profile_development") {
      li.include_setup = includeSetup;
      li.months = Number(months) || 0;
    } else if (productKey === "personal_capital_strategy" || productKey === "business_capital_strategy") {
      li.capital_secured_cents = Math.round(Number(capitalSecuredDollars) * 100);
    } else if (productKey === "profile_positioning_services") {
      li.placements = Number(placements) || 0;
    } else if (productKey === "grant_readiness_and_funding_research") {
      li.include_research = includeResearch;
      li.applications = Number(applications) || 0;
    } else if (productKey === "business_foundation_and_entity_setup") {
      li.state_filing_fee_cents = Math.round(Number(stateFilingFeeDollars) * 100);
    }
    return [li];
  }, [productKey, months, includeSetup, capitalSecuredDollars, placements, applications, includeResearch, stateFilingFeeDollars]);

  useEffect(() => {
    let alive = true;
    setError("");
    axios.post(`${API}/billing/preview`, { lines_input: linesInput }, tokenCfg())
      .then((r) => { if (alive) setPreview(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Preview failed"); });
    return () => { alive = false; };
  }, [linesInput]);

  const create = async () => {
    if (!clientName || !clientId) { setError("Client name and ID are required to create an invoice."); return; }
    setCreating(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/billing/invoices`, {
        lines_input: linesInput,
        client_id: clientId,
        client_name: clientName,
      }, tokenCfg());
      onCreated?.(data);
      setClientName(""); setClientId("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card data-testid="billing-calculator" className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Calculator size={18} className="text-[#6E5022]" weight="bold" />
        <h3 className="font-heading text-[#2A1C15] text-xl">Invoice Calculator</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="bj-label block mb-2">Product</label>
            <select
              data-testid="calc-product-select"
              value={productKey}
              onChange={(e) => setProductKey(e.target.value)}
              className="w-full bg-[#DCCFB9] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm"
            >
              {PRODUCTS.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
            </select>
          </div>

          {productKey === "financial_profile_development" && (
            <>
              <label className="flex items-center gap-2 text-sm text-[#2A1C15]">
                <input type="checkbox" data-testid="calc-include-setup" checked={includeSetup} onChange={(e) => setIncludeSetup(e.target.checked)} className="accent-[#6E5022]" />
                Include $199 setup fee
              </label>
              <div>
                <label className="bj-label block mb-2">Recurring months</label>
                <input type="number" min="0" max="24" data-testid="calc-months" value={months} onChange={(e) => setMonths(e.target.value)} className="w-full bg-[#DCCFB9] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm bj-tnum" />
              </div>
            </>
          )}
          {(productKey === "personal_capital_strategy" || productKey === "business_capital_strategy") && (
            <div>
              <label className="bj-label block mb-2">Capital secured ($)</label>
              <input type="number" min="0" step="0.01" data-testid="calc-capital" value={capitalSecuredDollars} onChange={(e) => setCapitalSecuredDollars(e.target.value)} className="w-full bg-[#DCCFB9] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm bj-tnum" />
              <div className="text-[#5E4D40] text-xs mt-1">10% contingency · $500 minimum</div>
            </div>
          )}
          {productKey === "profile_positioning_services" && (
            <div>
              <label className="bj-label block mb-2">Accounts</label>
              <input type="number" min="0" max="20" data-testid="calc-placements" value={placements} onChange={(e) => setPlacements(e.target.value)} className="w-full bg-[#DCCFB9] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm bj-tnum" />
              <div className="text-[#5E4D40] text-xs mt-1">$497 each · $497 floor</div>
            </div>
          )}
          {productKey === "grant_readiness_and_funding_research" && (
            <>
              <label className="flex items-center gap-2 text-sm text-[#2A1C15]">
                <input type="checkbox" data-testid="calc-include-research" checked={includeResearch} onChange={(e) => setIncludeResearch(e.target.checked)} className="accent-[#6E5022]" />
                Include $497 research fee
              </label>
              <div>
                <label className="bj-label block mb-2">Applications</label>
                <input type="number" min="0" max="20" data-testid="calc-apps" value={applications} onChange={(e) => setApplications(e.target.value)} className="w-full bg-[#DCCFB9] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm bj-tnum" />
                <div className="text-[#5E4D40] text-xs mt-1">$997 each</div>
              </div>
            </>
          )}
          {productKey === "business_foundation_and_entity_setup" && (
            <div>
              <label className="bj-label block mb-2">State filing fee ($, pass-through)</label>
              <input type="number" min="0" step="0.01" data-testid="calc-state-fee" value={stateFilingFeeDollars} onChange={(e) => setStateFilingFeeDollars(e.target.value)} className="w-full bg-[#DCCFB9] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm bj-tnum" />
              <div className="text-[#5E4D40] text-xs mt-1">$497 setup · state fee excluded from 3% processing fee</div>
            </div>
          )}

          <div className="pt-4 border-t border-[rgba(42,28,21,0.18)]">
            <label className="bj-label block mb-2">Client</label>
            <input type="text" data-testid="calc-client-name" placeholder="Client name" value={clientName} onChange={(e) => setClientName(e.target.value)} className="w-full bg-[#DCCFB9] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm mb-2" />
            <input type="text" data-testid="calc-client-id" placeholder="Client ID (from portfolio)" value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full bg-[#DCCFB9] border border-[rgba(42,28,21,0.18)] px-3 py-2 text-sm bj-tnum" />
          </div>
        </div>

        <div className="bg-[#DCCFB9] p-5 border border-[rgba(42,28,21,0.18)]">
          <div className="bj-label mb-3">Live totals</div>
          {!preview && <div className="text-[#5E4D40] text-sm">Computing…</div>}
          {preview && (
            <div className="space-y-2 text-sm">
              {preview.lines.map((li, i) => (
                <div key={`${li.product_key}-${li.description}-${i}`} className="flex justify-between gap-3">
                  <div className={`${li.is_pass_through ? "text-[#5E4D40] italic" : "text-[#2A1C15]"}`}>{relabel(li.description)}</div>
                  <div className="bj-tnum text-[#2A1C15]">{money(li.amount_cents)}</div>
                </div>
              ))}
              <div className="border-t border-[rgba(28,42,34,0.15)] pt-2 mt-3 space-y-1 text-xs">
                <div className="flex justify-between text-[#5E4D40]"><span>Services subtotal</span><span className="bj-tnum">{money(preview.totals.services_subtotal_cents)}</span></div>
                <div className="flex justify-between text-[#5E4D40]" data-testid="preview-processing-fee">
                  <span>Card processing fee (3%)<br/><span className="text-[0.75rem] italic">Processed via PaymentCloud + Authorize.net</span></span>
                  <span className="bj-tnum">{money(preview.totals.processing_fee_cents)}</span>
                </div>
                {preview.totals.pass_through_cents > 0 && (
                  <div className="flex justify-between text-[#5E4D40] italic"><span>Pass-through</span><span className="bj-tnum">{money(preview.totals.pass_through_cents)}</span></div>
                )}
              </div>
              <div className="flex justify-between border-t-2 border-[#C8A46A] pt-2 mt-2 text-lg" data-testid="preview-total">
                <span className="font-heading text-[#14281E]">Total</span>
                <span className="font-heading text-[#6E5022] bj-tnum">{money(preview.totals.total_cents)}</span>
              </div>
              <div className="text-[#5E4D40] text-xs">Status: <StatusPill status={preview.totals.status} /></div>
            </div>
          )}
          {error && <div data-testid="calc-error" className="text-[#5C1A24] text-xs mt-3 border border-[#5C1A24]/30 px-3 py-2">{error}</div>}
          <button
            data-testid="calc-create-invoice"
            disabled={creating || !preview}
            onClick={create}
            className="mt-4 w-full bg-[#2A1C15] hover:bg-[#14281E] text-[#DCCFB9] font-heading tracking-[0.18em] uppercase text-xs py-3 disabled:opacity-60"
          >
            {creating ? "Creating…" : "Create Invoice"}
          </button>
        </div>
      </div>
    </Card>
  );
};
